import { useCallback, type MutableRefObject, type Ref } from "react";
import { Eye } from "lucide-react";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { useConfiguratorContext } from "../context/DashboardConfiguratorContext";
import { TILE_COLORS } from "../constants";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import type { TileConfig } from "../types";
import type { WidgetId } from "@/other/constants/EntityManagerConfig";
import { cn } from "@/modern/utils";

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === "function") {
    ref(value);
  } else {
    (ref as MutableRefObject<T | null>).current = value;
  }
}

export function HiddenTilesSidebar({
  sidebarRef,
}: {
  sidebarRef?: Ref<HTMLDivElement>;
}) {
  const { hiddenTiles, handleToggleVisibility } = useConfiguratorContext();

  const { setNodeRef, isOver } = useDroppable({ id: "hidden-droppable" });

  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      assignRef(sidebarRef, node);
    },
    [setNodeRef, sidebarRef]
  );

  return (
    <div
      ref={setRootRef}
      className={cn(
        "w-[180px] border-l border-border bg-background/50 flex flex-col flex-shrink-0 transition-colors",
        isOver && "ring-2 ring-inset ring-primary/40 bg-primary/5"
      )}
    >
      <div className="px-3 pt-3 pb-2">
        <label className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          Hidden
        </label>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-3 pb-3 flex flex-col gap-2">
          {hiddenTiles.length === 0 && (
            <p className={cn(
              "text-[11px] text-muted-foreground/60 py-4 text-center",
              isOver && "text-primary/60"
            )}>
              {isOver ? "Drop here to hide" : "No hidden tiles"}
            </p>
          )}
          {hiddenTiles.map((tile) => (
            <DraggableHiddenTile
              key={tile.id}
              tile={tile}
              onToggleVisibility={handleToggleVisibility}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

function DraggableHiddenTile({
  tile,
  onToggleVisibility,
}: {
  tile: TileConfig;
  onToggleVisibility: (id: WidgetId) => void;
}) {
  const bgColor = TILE_COLORS[tile.color] ?? TILE_COLORS.blue;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: tile.id,
    data: { fromHidden: true },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        "relative group rounded-md overflow-hidden cursor-grab touch-none select-none",
        isDragging && "opacity-30 cursor-grabbing"
      )}
      style={{ minHeight: 60 }}
    >
      <div
        className="absolute inset-0 rounded-md"
        style={{ backgroundColor: bgColor, opacity: 0.72 }}
      />
      <div className="relative h-full min-h-[60px] flex items-center justify-center px-3">
        <span className="text-[11px] font-medium text-white/90 truncate">
          {tile.title}
        </span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility(tile.id);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-80 transition-opacity p-1.5 rounded hover:bg-black/20"
        title="Show tile"
      >
        <Eye className="w-4 h-4 text-white/70" />
      </button>
    </div>
  );
}
